/**
 * ScopeOps Landing Page
 * Features overview, template downloads, and quick links to ScopeOps tools
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  FileText, Download, Sparkles, ClipboardList, Target,
  FileSpreadsheet, Users, ArrowRight, CheckCircle2, 
  BookOpen, Layers, BarChart3, Shield
} from 'lucide-react';
import { trackEvent } from '../utils/telemetry';

// Template data
const TEMPLATES = [
  {
    id: 'intake-docx',
    name: 'Proposal Intake Form',
    description: 'Structured form to capture all project inputs for proposals',
    format: 'DOCX',
    icon: FileText,
    path: '/downloads/scopeops/templates/proposal-intake-form.docx',
    cta: 'scopeops-template-intake-docx',
    size: '38 KB'
  },
  {
    id: 'discovery-pdf',
    name: 'Discovery Interview Script',
    description: '45-60 minute guided script for client discovery sessions',
    format: 'PDF',
    icon: Users,
    path: '/downloads/scopeops/templates/discovery-interview-script.pdf',
    cta: 'scopeops-template-discovery-pdf',
    size: '6 KB'
  },
  {
    id: 'reqs-excel',
    name: 'Requirements & Acceptance Sheet',
    description: 'Starter spreadsheet with validation for requirements capture',
    format: 'XLSX',
    icon: FileSpreadsheet,
    path: '/downloads/scopeops/templates/requirements-acceptance-starter.xlsx',
    cta: 'scopeops-template-reqs-excel',
    size: '6 KB'
  }
];

// Features data
const FEATURES = [
  {
    title: 'AI Scoping Assistant',
    description: 'Get intelligent guidance on scope, estimation, and governance',
    icon: Sparkles,
    link: '/ai-scoping-assistant',
    color: 'from-purple-500 to-indigo-600'
  },
  {
    title: 'Scope Baselines',
    description: 'Track and version scope changes with full audit trail',
    icon: Layers,
    link: '/scope-baselines',
    color: 'from-blue-500 to-cyan-600'
  },
  {
    title: 'Proposal Builder',
    description: 'Create professional proposals with AI-assisted estimation',
    icon: FileText,
    link: '/consulting',
    color: 'from-green-500 to-emerald-600'
  },
  {
    title: 'Acceptance & KPIs',
    description: 'Define measurable success criteria and track outcomes',
    icon: Target,
    link: '/ai-scoping-assistant',
    color: 'from-orange-500 to-amber-600'
  }
];

/**
 * Template Card Component
 */
const TemplateCard = ({ template }) => {
  const Icon = template.icon;
  
  const handleDownload = () => {
    // Track CTA click
    trackEvent('cta_click', { cta: template.cta });
    
    // Track template download
    trackEvent('template_download', {
      templateName: template.name,
      page: 'scopeops',
      timestamp: new Date().toISOString()
    });
    
    // Trigger download
    const link = document.createElement('a');
    link.href = template.path;
    link.download = template.path.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div 
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-purple-200 transition-all group"
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-lg bg-purple-50 flex items-center justify-center flex-shrink-0 group-hover:bg-purple-100 transition-colors">
          <Icon className="w-6 h-6 text-purple-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 truncate">{template.name}</h3>
            <span className="px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-600 rounded">
              {template.format}
            </span>
          </div>
          <p className="text-sm text-gray-500 mb-3">{template.description}</p>
          <button
            onClick={handleDownload}
            data-cta={template.cta}
            data-testid={`download-${template.id}`}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-purple-600 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
          >
            <Download className="w-4 h-4" />
            Download ({template.size})
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Feature Card Component
 */
const FeatureCard = ({ feature }) => {
  const navigate = useNavigate();
  const Icon = feature.icon;
  
  return (
    <div 
      onClick={() => navigate(feature.link)}
      className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-lg hover:border-purple-200 transition-all cursor-pointer group"
    >
      <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-4`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
      <h3 className="font-semibold text-gray-900 mb-2 group-hover:text-purple-600 transition-colors">
        {feature.title}
      </h3>
      <p className="text-sm text-gray-500 mb-3">{feature.description}</p>
      <div className="flex items-center gap-1 text-sm text-purple-600 font-medium">
        <span>Explore</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
      </div>
    </div>
  );
};

/**
 * Main ScopeOps Page
 */
const ScopeOpsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen bg-gray-50" data-testid="scopeops-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-purple-600 via-indigo-600 to-purple-700 text-white">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="flex items-center gap-2 mb-4">
            <ClipboardList className="w-8 h-8" />
            <span className="text-purple-200 font-medium">ScopeOps™</span>
          </div>
          <h1 className="text-4xl font-bold mb-4">
            Scope Management, Simplified
          </h1>
          <p className="text-xl text-purple-100 max-w-2xl mb-8">
            From discovery to proposal, ScopeOps gives you the tools 
            to capture requirements, define success criteria, and build winning proposals.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/consulting')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-700 font-semibold rounded-lg hover:bg-purple-50 transition-colors"
              data-testid="start-proposal-btn"
            >
              <FileText className="w-5 h-5" />
              Start a Proposal
            </button>
            <button
              onClick={() => navigate('/ai-scoping-assistant')}
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 text-white font-semibold rounded-lg hover:bg-purple-400 transition-colors"
            >
              <Sparkles className="w-5 h-5" />
              Try AI Assistant
            </button>
          </div>
        </div>
      </div>
      
      {/* Features Grid */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">ScopeOps Tools</h2>
        <p className="text-gray-500 mb-8">Everything you need to capture, refine, and manage project scope</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {FEATURES.map((feature, idx) => (
            <FeatureCard key={idx} feature={feature} />
          ))}
        </div>
        
        {/* Templates Section */}
        <div className="bg-white rounded-2xl border border-gray-200 p-8" data-testid="templates-section">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Templates</h2>
              <p className="text-sm text-gray-500">Jumpstart your proposal process</p>
            </div>
          </div>
          
          <p className="text-gray-600 mb-6 max-w-2xl">
            Download our professionally designed templates to streamline your discovery 
            sessions, capture requirements, and accelerate proposal creation.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {TEMPLATES.map((template) => (
              <TemplateCard key={template.id} template={template} />
            ))}
          </div>
        </div>
        
        {/* Benefits Section */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Structured Capture</h3>
              <p className="text-sm text-gray-500">
                Never miss critical inputs with guided forms and checklists
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
              <BarChart3 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">AI-Powered Estimation</h3>
              <p className="text-sm text-gray-500">
                Get intelligent effort estimates based on scope complexity
              </p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Governance Ready</h3>
              <p className="text-sm text-gray-500">
                Built-in KPIs, acceptance criteria, and audit trails
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScopeOpsPage;
